import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Context as AuthContext } from "../../context/Auth";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha, useTheme } from "@mui/material/styles";

import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";

import {
    useService as useBillingService,
    SubscriptionPlan,
    PlanMetadata,
} from "../../services/Billing";
import SectionCard from "../../components/SectionCard";
import Button from "../../components/Button";
import ErrorAlert from "../../components/ErrorAlert";

const PLAN_TIER: Record<SubscriptionPlan, number> = {
    [SubscriptionPlan.BASIC]: 1,
    [SubscriptionPlan.PRO]: 2,
    /* [SubscriptionPlan.ULTRA]: 3, */
};

function getPlanAction(
    plan: PlanMetadata,
    currentPlan: SubscriptionPlan | null,
): {
    label: string;
    variant: "contained" | "outlined" | "text";
    disabled: boolean;
} {
    if (!currentPlan) {
        return {
            label: "Subscribe",
            variant: "contained",
            disabled: false,
        };
    }

    if (currentPlan === plan.id) {
        return { label: "Current Plan", variant: "outlined", disabled: true };
    }

    const currentTier = PLAN_TIER[currentPlan];
    const planTier = PLAN_TIER[plan.id];

    if (planTier > currentTier) {
        return { label: "Upgrade", variant: "contained", disabled: false };
    }

    // Downgrades are now enabled - will take effect at next billing cycle
    return { label: "Downgrade", variant: "outlined", disabled: false };
}

interface PlanCardProps {
    plan: PlanMetadata;
    currentPlan: SubscriptionPlan | null;
    onSubscribe: (plan: SubscriptionPlan) => void;
    onDowngrade?: (plan: SubscriptionPlan) => void;
    loading: boolean;
    highlighted?: boolean;
}

function PlanCard({
    plan,
    currentPlan,
    onSubscribe,
    onDowngrade,
    loading,
    highlighted = false,
}: PlanCardProps) {
    const theme = useTheme();
    const action = getPlanAction(plan, currentPlan);
    const isCurrentPlan = currentPlan === plan.id;

    // Only grey out the current plan, not lower-tier plans
    const isGreyedOut = isCurrentPlan;

    const handleAction = () => {
        if (action.label === "Downgrade" && onDowngrade) {
            onDowngrade(plan.id);
        } else if (action.label === "Upgrade" || action.label === "Subscribe") {
            onSubscribe(plan.id);
        }
    };

    return (
        <Card
            elevation={highlighted ? 4 : 1}
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 2,
                border: "1px solid",
                borderColor: highlighted
                    ? theme.palette.primary.main
                    : alpha(theme.palette.common.white, 0.06),
                backgroundColor: highlighted
                    ? alpha(theme.palette.primary.main, 0.05)
                    : theme.palette.background.paper,
                position: "relative",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                    borderColor: isGreyedOut
                        ? alpha(theme.palette.common.white, 0.06)
                        : theme.palette.primary.main,
                },
            }}
        >
            {isCurrentPlan && (
                <Chip
                    label="Current Plan"
                    color="primary"
                    size="small"
                    sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                    }}
                />
            )}
            <CardContent
                sx={{
                    flexGrow: 1,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {plan.name}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, minHeight: 40 }}
                >
                    {plan.description}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    {plan.price}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                >
                    {(plan.characterLimit / 1000).toLocaleString()}k characters per month
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List dense sx={{ mb: 3, flexGrow: 1 }}>
                    {plan.features.map((feature, index) => (
                        <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={feature}
                                primaryTypographyProps={{
                                    variant: "body2",
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
                <Box sx={{ mt: "auto", pt: 2 }}>
                    <Button
                        variant={action.variant}
                        onClick={handleAction}
                        disabled={action.disabled || loading || plan.disabled}
                        startIcon={<CreditCardIcon />}
                        fullWidth
                    >
                        {action.label}
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function DashboardBilling() {
    const navigate = useNavigate();
    const { state: authState } = useContext(AuthContext);
    const billingService = useBillingService();
    const [loading, setLoading] = useState(true);
    const isAuthenticated = Boolean(authState.user);
    const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan | null>(
        null,
    );
    const [plans, setPlans] = useState<PlanMetadata[]>([]);
    const [hasUnlimitedAccess, setHasUnlimitedAccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        // Check for query parameters from Stripe redirect
        const searchParams = new URLSearchParams(window.location.search);
        const success = searchParams.get("success");
        const canceled = searchParams.get("canceled");

        if (success === "true") {
            setSuccessMessage(
                "Subscription started! Your credits have been refilled.",
            );
            navigate("/dashboard/pricing", { replace: true });
        } else if (canceled === "true") {
            setError("Checkout was canceled. No charges were made.");
            navigate("/dashboard/pricing", { replace: true });
        }

        const fetchData = async () => {
            try {
                setLoading(true);
                if (!success && !canceled) {
                    setError(null);
                }

                // Plans endpoint is now public - always fetch plans
                // Only fetch subscription info if authenticated (for highlighting current plan)
                const [plansData, subscriptionData] = await Promise.all([
                    billingService.getPlans(),
                    isAuthenticated
                        ? billingService.getSubscription()
                        : Promise.resolve({
                              subscription: null,
                              plan: null,
                              hasUnlimitedAccess: false,
                          }),
                ]);

                setCurrentPlan(subscriptionData.plan || plansData.currentPlan);
                setHasUnlimitedAccess(
                    subscriptionData.hasUnlimitedAccess ||
                        plansData.hasUnlimitedAccess ||
                        false,
                );
                setPlans(plansData.plans);
            } catch (err: any) {
                console.error("Failed to fetch billing data", err);
                // Only show error if authenticated, or if it's not an auth error
                if (
                    isAuthenticated ||
                    !err.message?.includes("authentication")
                ) {
                    if (!canceled) {
                        setError(
                            err.message || "Failed to load billing information",
                        );
                    }
                }
                // If not authenticated, just don't show subscription info
                if (!isAuthenticated) {
                    setCurrentPlan(null);
                    setHasUnlimitedAccess(false);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [billingService, navigate, isAuthenticated]);

    const handleSubscribe = async (plan: SubscriptionPlan) => {
        // If not authenticated, redirect to login
        if (!isAuthenticated) {
            navigate("/login");
            return;
        }

        try {
            setLoading(true);
            const successUrl = `${window.location.origin}/dashboard/pricing?success=true`;
            const cancelUrl = `${window.location.origin}/dashboard/pricing?canceled=true`;
            const { url } = await billingService.createCheckoutSession({
                plan,
                successUrl,
                cancelUrl,
            });
            window.location.href = url;
        } catch (err: any) {
            console.error("Failed to create checkout session", err);
            setError(err.message || "Failed to start checkout");
            setLoading(false);
        }
    };

    const handleDowngrade = async (plan: SubscriptionPlan) => {
        // If not authenticated, redirect to login
        if (!isAuthenticated) {
            navigate("/login");
            return;
        }

        try {
            setLoading(true);
            setError(null);
            await billingService.updateSubscriptionPlan({ plan });
            const planName = plans.find((p) => p.id === plan)?.name || plan;
            setSuccessMessage(
                `Plan downgraded to ${planName}. The change will take effect at the end of your current billing cycle.`,
            );
            // Refresh subscription data
            const subscriptionData = await billingService.getSubscription();
            setCurrentPlan(subscriptionData.plan);
            setLoading(false);
        } catch (err: any) {
            console.error("Failed to downgrade plan", err);
            setError(err.message || "Failed to downgrade plan");
            setLoading(false);
        }
    };

    // Find highlighted plan (current plan, or first plan if no subscription)
    const highlightedPlanIndex = currentPlan
        ? plans.findIndex((p) => p.id === currentPlan)
        : 1;

    return (
        <Container
            disableGutters
            sx={{
                width: "100%",
                mx: "auto",
                maxWidth: {
                    xs: "100%",
                    sm: 720,
                    md: 760,
                },
                mt: { xs: 1, sm: 2 },
                pb: { xs: 2, sm: 3 },
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {successMessage && (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 1,
                            backgroundColor: "success.light",
                            color: "success.contrastText",
                        }}
                    >
                        <Typography variant="body2">
                            {successMessage}
                        </Typography>
                    </Box>
                )}

                {error && <ErrorAlert message={error} />}

                {/* Unlimited Access Card */}
                {hasUnlimitedAccess && (
                    <SectionCard title="Account Status">
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mt: 4,
                                mb: 2,
                            }}
                        >
                            <CheckCircleIcon color="success" />
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Unlimited Access
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Your account has unlimited access to all
                                    features. No subscription required.
                                </Typography>
                            </Box>
                        </Box>
                    </SectionCard>
                )}

                {/* Plan Cards - Always show plans as individual cards */}
                {!hasUnlimitedAccess && (
                    <>
                        {loading && plans.length === 0 ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    py: 4,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={3}>
                                {plans.map((plan, index) => (
                                    <Grid item xs={12} md={6} key={plan.id}>
                                        <PlanCard
                                            plan={plan}
                                            currentPlan={currentPlan}
                                            onSubscribe={handleSubscribe}
                                            onDowngrade={handleDowngrade}
                                            loading={loading}
                                            highlighted={
                                                index === highlightedPlanIndex
                                            }
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                )}
            </Box>
        </Container>
    );
}
